import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Building2, Power, ChevronDown, ChevronRight, MapPin, UserPlus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useVenue } from "@/context/VenueContext";

export default function PlusPlatform() {
  const navigate = useNavigate();
  const { reload } = useVenue();
  const [groups, setGroups] = useState([]);
  const [expanded, setExpanded] = useState({});     // group_id -> venues[]
  const [showNew, setShowNew] = useState(false);
  const [ng, setNg] = useState({ nome: "", owner_email: "", owner_password: "", owner_nome: "" });
  const [nv, setNv] = useState({});                  // group_id -> new venue name
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/platform/groups"); setGroups(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createGroup = async () => {
    if (!ng.nome.trim()) { toast.error("Nome gruppo obbligatorio"); return; }
    if ((ng.owner_email && !ng.owner_password) || (!ng.owner_email && ng.owner_password)) {
      toast.error("Per il titolare servono sia email che password"); return;
    }
    setBusy(true);
    try {
      await api.post("/platform/groups", {
        nome: ng.nome.trim(),
        ...(ng.owner_email ? { owner_email: ng.owner_email, owner_password: ng.owner_password, owner_nome: ng.owner_nome } : {}),
      });
      toast.success("Gruppo creato");
      setNg({ nome: "", owner_email: "", owner_password: "", owner_nome: "" });
      setShowNew(false);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const toggleGroup = async (g) => {
    try { await api.patch(`/platform/groups/${g.id}`, { stato: g.stato === "ACTIVE" ? "SUSPENDED" : "ACTIVE" }); await load(); await reload?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const loadVenues = async (gid) => {
    if (expanded[gid]) { setExpanded((s) => ({ ...s, [gid]: undefined })); return; }
    try { const { data } = await api.get(`/platform/venues?group_id=${gid}`); setExpanded((s) => ({ ...s, [gid]: data })); }
    catch (e) { toast.error(apiError(e)); }
  };

  const createVenue = async (gid) => {
    const nome = (nv[gid] || "").trim();
    if (!nome) return;
    try {
      await api.post("/platform/venues", { group_id: gid, nome });
      setNv((s) => ({ ...s, [gid]: "" }));
      const { data } = await api.get(`/platform/venues?group_id=${gid}`);
      setExpanded((s) => ({ ...s, [gid]: data }));
      await load(); await reload?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleVenue = async (gid, v) => {
    try {
      await api.patch(`/platform/venues/${v.id}`, { stato: v.stato === "ACTIVE" ? "SUSPENDED" : "ACTIVE" });
      const { data } = await api.get(`/platform/venues?group_id=${gid}`);
      setExpanded((s) => ({ ...s, [gid]: data }));
      await reload?.();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="flex items-center gap-3 mb-8">
          <button data-testid="platform-back" onClick={() => navigate("/")} className="p-2 rounded-lg border border-zinc-800 hover:border-zinc-600"><ArrowLeft className="w-4 h-4" /></button>
          <Building2 className="w-6 h-6 text-sky-400" />
          <h1 className="font-display text-2xl font-bold">Console Platform</h1>
          <span className="ml-auto text-xs text-zinc-500">Main · gestione network</span>
        </div>

        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-zinc-400">{groups.length} gruppi nel network</div>
          <button data-testid="new-group-btn" onClick={() => setShowNew((s) => !s)} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-400 text-white font-semibold text-sm"><Plus className="w-4 h-4" /> Nuovo gruppo</button>
        </div>

        {showNew && (
          <div data-testid="new-group-form" className="rounded-xl bg-zinc-900 border border-sky-500/30 p-4 mb-6">
            <div className="text-sm font-semibold mb-3">Nuovo gruppo &amp; titolare</div>
            <input data-testid="ng-nome" value={ng.nome} onChange={(e) => setNg({ ...ng, nome: e.target.value })} placeholder="Nome gruppo (es. Gruppo Rossi)" className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500 mb-3" />
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Titolare (opzionale)</div>
            <div className="grid sm:grid-cols-3 gap-2 mb-3">
              <input data-testid="ng-owner-nome" value={ng.owner_nome} onChange={(e) => setNg({ ...ng, owner_nome: e.target.value })} placeholder="Nome titolare" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500" />
              <input data-testid="ng-owner-email" value={ng.owner_email} onChange={(e) => setNg({ ...ng, owner_email: e.target.value })} placeholder="Email titolare" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500" />
              <input data-testid="ng-owner-password" type="password" value={ng.owner_password} onChange={(e) => setNg({ ...ng, owner_password: e.target.value })} placeholder="Password (min 6)" className="bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500" />
            </div>
            <button data-testid="create-group-btn" disabled={busy} onClick={createGroup} className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-semibold text-sm disabled:opacity-50">Crea gruppo</button>
          </div>
        )}

        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id} data-testid={`platform-group-${g.id}`} className="rounded-xl bg-zinc-900 border border-zinc-800">
              <div className="flex items-center gap-3 px-4 py-3">
                <button data-testid={`group-expand-${g.id}`} onClick={() => loadVenues(g.id)} className="p-1 rounded hover:bg-zinc-800">{expanded[g.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
                <span className={`w-2 h-2 rounded-full ${g.stato === "ACTIVE" ? "bg-emerald-400" : "bg-red-500"}`} />
                <div className="flex-1">
                  <div className="font-semibold">{g.nome} {g.is_default && <span className="text-[10px] text-zinc-500">(default)</span>}</div>
                  <div className="text-xs text-zinc-500">{g.owner?.email || "nessun titolare"} · <span className="text-emerald-400">{g.active_venue_count ?? g.venue_count}/{g.venue_count}</span> sale attive · <span className="text-sky-400">{g.customer_count ?? 0}</span> clienti</div>
                </div>
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${g.stato === "ACTIVE" ? "text-emerald-300 bg-emerald-500/10" : "text-red-300 bg-red-500/10"}`}>{g.stato === "ACTIVE" ? "Attivo" : "Sospeso"}</span>
                {!g.is_default && (
                  <button data-testid={`group-toggle-${g.id}`} onClick={() => toggleGroup(g)} className="p-1.5 rounded-lg border border-zinc-800 hover:border-zinc-600" title="Attiva/Sospendi"><Power className="w-3.5 h-3.5" /></button>
                )}
              </div>
              {expanded[g.id] && (
                <div className="px-4 pb-4 pl-12 border-t border-zinc-800 pt-3">
                  <div className="space-y-2 mb-3">
                    {(expanded[g.id] || []).map((v) => (
                      <div key={v.id} data-testid={`platform-venue-${v.id}`} className="flex items-center gap-2 text-sm">
                        <MapPin className="w-3.5 h-3.5 text-sky-400" />
                        <span className="flex-1">{v.nome}</span>
                        <span className={`text-[10px] uppercase ${v.stato === "ACTIVE" ? "text-emerald-400" : "text-red-400"}`}>{v.stato === "ACTIVE" ? "attiva" : "sospesa"}</span>
                        <button data-testid={`platform-venue-toggle-${v.id}`} onClick={() => toggleVenue(g.id, v)} className="p-1 rounded border border-zinc-800 hover:border-zinc-600"><Power className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {(expanded[g.id] || []).length === 0 && <div className="text-xs text-zinc-600">Nessuna sala.</div>}
                  </div>
                  <div className="flex gap-2">
                    <input data-testid={`pv-input-${g.id}`} value={nv[g.id] || ""} onChange={(e) => setNv((s) => ({ ...s, [g.id]: e.target.value }))} placeholder="Nome nuova sala…" className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm outline-none focus:border-sky-500" />
                    <button data-testid={`pv-add-${g.id}`} onClick={() => createVenue(g.id)} className="flex items-center gap-1.5 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-sm"><Plus className="w-4 h-4" /> Sala</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
